import React, { useState, useEffect, useRef } from 'react';
import { Alert } from '../../utils/Alert';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../../components/layout/MainLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StudentSidebar from './components/StudentSidebar';
import TeacherSidebar from '../teacher/sidebar/TeacherSidebar';
import { useAuth } from '../../contexts/AuthContext';


import { API_URL } from '../../config';
import { renderMathHtml } from '../../utils/renderMath';
interface DashboardPageProps {
  user: {
    name: string;
    role: string;
    avatar?: string;
  };
  onLogout: () => void;
}

const ExamResult: React.FC<DashboardPageProps> = ({ user, onLogout }) => {
  const { examId } = useParams<{ examId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // Check if this is being viewed by a teacher
  const fromTeacher = location.state?.fromTeacher || false;
  const studentName = location.state?.studentName;
  const studentEmail = location.state?.studentEmail;
  const studentExamId = location.state?.studentExamId || examId;
  const examTitle = location.state?.examTitle;
  // const preloadedAttempts = location.state?.preloadedAttempts || [];
  
  const [results, setResults] = useState<any[]>([]); // Array of attempts
  const [selectedAttemptIdx, setSelectedAttemptIdx] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalQuestion, setModalQuestion] = useState<any | null>(null);
  const [solutionHtml, setSolutionHtml] = useState<string | null>(null);
  const [isSolutionLoading, setIsSolutionLoading] = useState(false);
  const [solutionError, setSolutionError] = useState<string | null>(null);
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const [countdown, setCountdown] = useState<string | null>(null);

  useEffect(() => {
    const fetchExamResult = async () => {
      if (!studentExamId) {
        Alert.error('Oops...', 'Missing exam information');
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        if (!token) {
          Alert.error('Oops...', 'Authentication token not found. Please log in again.');
          return;
        }
        
        const response = await axios.get(
          `${API_URL}/api/student-exams/${studentExamId}/result?recalculate=false`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'accept': 'application/json'
            }
          }
        );
        
        // If response is an array, set as results; else wrap in array
        let data = response.data;
        if (Array.isArray(data)) {
          setResults(data);
        } else {
          setResults([data]);
        }
        
        // Optionally, process each result if needed
      } catch (err: any) {
        console.error('Error fetching exam result:', err);
        if (err.response?.status === 401) {
          Alert.error('Oops...', 'Authentication failed. Please log in again.');
        } else if (err.response?.status === 404) {
          Alert.error('Oops...', 'Exam result not found or you do not have access to it.');
        } else {
          Alert.error('Oops...', 'Failed to fetch exam result. Please try again later.');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchExamResult();
  }, [studentExamId, token]);

  // Removed fetching of all attempts since not used in UI currently

  // Fetch solution/note for selected question when modal opens
  useEffect(() => {
    const fetchSolution = async () => {
      if (!modalOpen || !modalQuestion?.question_id) {
        setSolutionHtml(null);
        setSolutionError(null);
        return;
      }
      if (!token) {
        setSolutionError('Authentication token not found. Please log in again.');
        return;
      }
      setIsSolutionLoading(true);
      setSolutionError(null);
      try {
        const resp = await axios.get(`${API_URL}/api/questions/${modalQuestion.question_id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'accept': 'application/json'
            }
        });
        setSolutionHtml(resp.data?.note || null);
      } catch (err: any) {
        if (err.response?.status === 401) {
          setSolutionError('Authentication failed. Please log in again.');
        } else if (err.response?.status === 404) {
          setSolutionError('Solution not found for this question.');
        } else {
          setSolutionError('Failed to load solution. Please try again later.');
        }
      } finally {
        setIsSolutionLoading(false);
      }
    };
    fetchSolution();
  }, [modalOpen, modalQuestion?.question_id, token]);

  // Live countdown when result is not yet published
  useEffect(() => {
    if (!results.length) return;
    const selected = results[selectedAttemptIdx];
    const publishAt = selected?.publish_time ? new Date(selected.publish_time) : null;
    const isNotPublished = selected?.status === 'not published' && !!publishAt;
    if (!isNotPublished) {
      setCountdown(null);
      return;
    }
    const update = () => {
      const now = new Date();
      const diffMs = publishAt!.getTime() - now.getTime();
      if (diffMs <= 0) {
        setCountdown('Available now');
        return;
      }
      const totalSeconds = Math.floor(diffMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const parts: string[] = [];
      if (hours > 0) parts.push(String(hours).padStart(2, '0'));
      parts.push(String(minutes).padStart(2, '0'));
      parts.push(String(seconds).padStart(2, '0'));
      setCountdown(parts.join(':'));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [results, selectedAttemptIdx]);

  // Ensure images within injected HTML don't overflow the modal
  useEffect(() => {
    if (!modalOpen) return;
    const container = modalContentRef.current;
    if (!container) return;
    const images = container.querySelectorAll<HTMLImageElement>('img');
    images.forEach((img) => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
    });
  }, [modalOpen, modalQuestion, solutionHtml]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBackClick = () => {
    if (fromTeacher) {
      // Navigate back to the teacher's exam results page
      navigate(`/teacher/exams/${examId}/results`);
    } else {
      // Student navigates back to their exams page
      navigate('/student/exams');
    }
  };
  
  // Helper to render answer details (used in modal)
  const renderAnswerDetails = (q: any) => {
    // Matrix Match UI
    if (q.question_type === 'matrix_match' && q.student_answer_raw && q.correct_answer_raw) {
      let rows = q.matrix_rows || q.rows || [];
      let columns = q.matrix_columns || q.columns || [];
      if ((!rows.length || !columns.length) && q.correct_answer) {
        if (Array.isArray(q.correct_answer)) {
          const rowSet = new Set();
          const colSet = new Set();
          q.correct_answer.forEach((m: any) => {
            if (m.row) rowSet.add(m.row);
            if (m.column) colSet.add(m.column);
          });
          rows = Array.from(rowSet);
          columns = Array.from(colSet);
        }
      }
      if (!rows.length) rows = ['Row 1', 'Row 2', 'Row 3'];
      if (!columns.length) columns = ['Col 1', 'Col 2', 'Col 3'];
      const studentMatches = (q.student_answer_raw.matches || []);
      const correctMatches = (q.correct_answer_raw.matches || []);
      const studentMap = new Map(studentMatches.map((m: any) => [String((m.row_id || m.row) + '-' + (m.column_id || m.column)), true]));
      const correctMap = new Map(correctMatches.map((m: any) => [String((m.row_id || m.row) + '-' + (m.column_id || m.column)), true]));
      return (
        <div>
          <div className="font-semibold mb-2">Matrix Match Table:</div>
          <div className="overflow-x-auto">
            <table className="min-w-full border bg-gray-50">
              <thead>
                <tr>
                  <th className="border px-2 py-1"></th>
                  {columns.map((col: any, i: number) => (
                    <th key={i} className="border px-2 py-1">
                      <div dangerouslySetInnerHTML={{ __html: renderMathHtml(typeof col === 'object' ? (col.text || String(col)) : String(col)) }} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row: any, rowIdx: number) => (
                  <tr key={rowIdx}>
                    <td className="border px-2 py-1 font-medium">
                      <div dangerouslySetInnerHTML={{ __html: renderMathHtml(typeof row === 'object' ? (row.text || String(row)) : String(row)) }} />
                    </td>
                    {columns.map((col: any, colIdx: number) => {
                      const key = String((row.id || rowIdx + 1) + '-' + (col.id || colIdx + 1));
                      const isStudent = studentMap.has(key);
                      const isCorrect = correctMap.has(key);
                      return (
                        <td key={colIdx} className="border px-2 py-1 text-center">
                          {isStudent && <span style={{ color: 'green', fontWeight: 'bold', marginRight: isCorrect ? 4 : 0 }}>✔️</span>}
                          {isCorrect && <span style={{ color: 'blue', fontWeight: 'bold' }}>●</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex gap-4 text-sm">
            <span><span style={{ color: 'green', fontWeight: 'bold' }}>✔️</span> Your Answer</span>
            <span><span style={{ color: 'blue', fontWeight: 'bold' }}>●</span> Correct Answer</span>
          </div>
        </div>
      );
    }
    // Three Column Match UI
    if (q.question_type === 'three_column_match' && Array.isArray(q.student_answer) && Array.isArray(q.correct_answer)) {
      const studentMatches = q.student_answer;
      const correctMatches = q.correct_answer;
      const rowCount = Math.max(studentMatches.length, correctMatches.length);
      // Try to get content arrays if available
      const qd = q.question_data || {};
      const columnA = Array.isArray(qd.column_a_items) ? qd.column_a_items : [];
      const columnB = Array.isArray(qd.column_b_items) ? qd.column_b_items : [];
      const columnC = Array.isArray(qd.column_c_items) ? qd.column_c_items : [];
      return (
        <div>
          <div className="font-semibold mb-2">Three Column Match Table:</div>
          <div className="overflow-x-auto">
            <table className="min-w-full border bg-gray-50">
              <thead>
                <tr>
                  <th className="border px-2 py-1">#</th>
                  <th className="border px-2 py-1">Student A</th>
                  <th className="border px-2 py-1">Student B</th>
                  <th className="border px-2 py-1">Student C</th>
                  <th className="border px-2 py-1">Correct A</th>
                  <th className="border px-2 py-1">Correct B</th>
                  <th className="border px-2 py-1">Correct C</th>
                  <th className="border px-2 py-1">Result</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: rowCount }).map((_, idx) => {
                  const student = studentMatches[idx] || {};
                  const correct = correctMatches[idx] || {};
                  // Use string values if present, else try to use indices with content arrays
                  const getVal = (val: any, arr: any[]) => {
                    if (typeof val === 'string') return val;
                    if (typeof val === 'number' && arr.length) return arr[val] || val;
                    return val ?? '';
                  };
                  const studentA = getVal(student.a, columnA);
                  const studentB = getVal(student.b, columnB);
                  const studentC = getVal(student.c, columnC);
                  const correctA = getVal(correct.a, columnA);
                  const correctB = getVal(correct.b, columnB);
                  const correctC = getVal(correct.c, columnC);
                  const isRowCorrect = studentA === correctA && studentB === correctB && studentC === correctC;
                  return (
                    <tr key={idx} style={{ background: isRowCorrect ? '#e6ffe6' : '#ffe6e6' }}>
                      <td className="border px-2 py-1">{idx + 1}</td>
                      <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(studentA ?? '')) }} /></td>
                      <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(studentB ?? '')) }} /></td>
                      <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(studentC ?? '')) }} /></td>
                      <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(correctA ?? '')) }} /></td>
                      <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(correctB ?? '')) }} /></td>
                      <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(correctC ?? '')) }} /></td>
                      <td className="border px-2 py-1 text-lg">
                        {isRowCorrect ? (
                          <span style={{ color: 'green' }}>✔</span>
                        ) : (
                          <span style={{ color: 'red' }}>✘</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex gap-4 text-sm">
            <span><span style={{ color: 'green', fontWeight: 'bold' }}>✔</span> Correct Row</span>
            <span><span style={{ color: 'red', fontWeight: 'bold' }}>✘</span> Incorrect Row</span>
          </div>
        </div>
      );
    }
    // Comprehension UI
    if (q.question_type === 'comprehension' && Array.isArray(q.sub_questions)) {
      const qd = q.question_data || {};
      const paragraphs = qd.comprehension_paragraphs || [];
      return (
        <div>
          {paragraphs.length > 0 && (
            <div className="mb-4">
              {paragraphs.map((p: any, i: number) => (
                <div key={i} className="bg-gray-100 rounded px-4 py-2 mb-2">
                  Paragraph {i + 1}
                  <br />
                  <span dangerouslySetInnerHTML={{ __html: renderMathHtml(p.paragraph_text) }} />
                </div>
              ))}
            </div>
          )}
          <div className="font-semibold mb-2">Sub-Questions:</div>
          {q.sub_questions.map((subq: any, i: number) => (
            <div key={subq.sub_question_id} className="mb-4 p-3 bg-white border rounded">
              {/* Render sub-question content as HTML if present */}
              <div className="font-medium ">
                Q{subq.sub_question_id}:
                {subq.content ? (
                  <span
                    className="ml-1"
                    dangerouslySetInnerHTML={{ __html: renderMathHtml(subq.content) }}
                  />
                ) : (
                  <span className="ml-1 text-gray-600">No content</span>
                )}
              </div>
              <div className="text-xs text-gray-600 ">
                Type: {subq.type ? subq.type.replace(/_/g, ' ') : 'N/A'}
              </div>
              <div>
                <span style={{ color: '#2563eb' }}>Your Answer:</span>{' '}
                {(() => {
                  if (subq.type === 'single_correct_1' && typeof subq.student_answer === 'string') {
                    const ans = subq.answers?.find((a: any) => a.option_label === subq.student_answer);
                    return (
                      <span>
                        {subq.student_answer}: {ans ? <span dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} /> : ''}
                        {subq.is_correct ? <span style={{ color: 'green', marginLeft: 8 }}>✔</span> : <span style={{ color: 'red', marginLeft: 8 }}>✘</span>}
                      </span>
                    );
                  }
                  if (subq.type === 'multiple_correct' && Array.isArray(subq.student_answer)) {
                    return (
                      <span>
                        {subq.student_answer.map((label: string) => {
                          const ans = subq.answers?.find((a: any) => a.option_label === label);
                          return (
                            <span key={label} style={{ marginRight: 8 }}>
                              {label}: {ans ? <span dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} /> : ''}
                            </span>
                          );
                        })}
                        {subq.is_correct ? <span style={{ color: 'green', marginLeft: 8 }}>✔</span> : <span style={{ color: 'red', marginLeft: 8 }}>✘</span>}
                      </span>
                    );
                  }
                  if ((subq.type === 'integer_type' || subq.type === 'numerical_type') && subq.student_answer !== null && subq.student_answer !== undefined) {
                    return (
                      <span>
                        {subq.student_answer}
                        {subq.is_correct ? <span style={{ color: 'green', marginLeft: 8 }}>✔</span> : <span style={{ color: 'red', marginLeft: 8 }}>✘</span>}
                      </span>
                    );
                  }
                  return <span className="text-gray-400">N/A</span>;
                })()}
              </div>
              <div>
                <span style={{ color: '#059669' }}>Correct Answer:</span>{' '}
                {(() => {
                  if (subq.type === 'single_correct_1' && subq.correct_answer?.label) {
                    const ans = subq.answers?.find((a: any) => a.option_label === subq.correct_answer.label);
                    return <span>{subq.correct_answer.label}: {ans ? <span dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} /> : ''}</span>;
                  }
                  if (subq.type === 'multiple_correct' && Array.isArray(subq.correct_answer?.labels)) {
                    return (
                      <span>
                        {subq.correct_answer.labels.map((label: string) => {
                          const ans = subq.answers?.find((a: any) => a.option_label === label);
                          return (
                            <span key={label} style={{ marginRight: 8 }}>
                              {label}: {ans ? <span dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} /> : ''}
                            </span>
                          );
                        })}
                      </span>
                    );
                  }
                  if ((subq.type === 'integer_type' || subq.type === 'numerical_type') && subq.correct_answer?.value !== null && subq.correct_answer?.value !== undefined) {
                    return <span>{subq.correct_answer.value}</span>;
                  }
                  return <span className="text-gray-400">N/A</span>;
                })()}
              </div>
            </div>
          ))}
        </div>
      );
    }
    // Default fallback (old logic)
    return (
      <div>
        <div className="mb-4">
          <h3 className="font-semibold mb-2">Your Answer</h3>
          {Array.isArray(q.student_answer)
            ? q.student_answer.map((ans: any, i: number) =>
                typeof ans === 'object' && ans !== null
                  ? (
                      <div key={i}>
                        {Object.entries(ans).map(([k, v]) => (
                          <React.Fragment key={k}>
                            <b>{k}:</b> <span dangerouslySetInnerHTML={{ __html: renderMathHtml(String(v)) }} />
                            <br />
                          </React.Fragment>
                        ))}
                      </div>
                    )
                  : (
                      <React.Fragment key={i}>
                        <span dangerouslySetInnerHTML={{ __html: renderMathHtml(String(ans)) }} />
                        <br />
                      </React.Fragment>
                    )
              )
            : q.student_answer && typeof q.student_answer === 'object'
              ? (
                  <div>
                    {Object.entries(q.student_answer).map(([k, v]) => (
                      <React.Fragment key={k}>
                        <b>{k}:</b> <span dangerouslySetInnerHTML={{ __html: String(v) }} />
                        <br />
                      </React.Fragment>
                    ))}
                  </div>
                )
              : q.student_answer === null
                ? <span className="text-gray-400">N/A</span>
                : <span dangerouslySetInnerHTML={{ __html: renderMathHtml(String(q.student_answer)) }} />
          }
        </div>
        <div>
          <h3 className="font-semibold mb-2">Correct Answer</h3>
          {Array.isArray(q.correct_answer)
            ? q.correct_answer.map((ans: any, i: number) =>
                typeof ans === 'object' && ans !== null
                  ? (
                      <div key={i}>
                        {Object.entries(ans).map(([k, v]) => (
                          <React.Fragment key={k}>
                            <b>{k}:</b> <span dangerouslySetInnerHTML={{ __html: renderMathHtml(String(v)) }} />
                            <br />
                          </React.Fragment>
                        ))}
                      </div>
                    )
                  : (
                      <React.Fragment key={i}>
                        <span dangerouslySetInnerHTML={{ __html: renderMathHtml(String(ans)) }} />
                        <br />
                      </React.Fragment>
                    )
              )
            : q.correct_answer && typeof q.correct_answer === 'object'
              ? (
                  <div>
                    {Object.entries(q.correct_answer).map(([k, v]) => (
                      <React.Fragment key={k}>
                        <b>{k}:</b> <span dangerouslySetInnerHTML={{ __html: String(v) }} />
                        <br />
                      </React.Fragment>
                    ))}
                  </div>
                )
              : q.correct_answer === null
                ? <span className="text-gray-400">N/A</span>
                : <span dangerouslySetInnerHTML={{ __html: renderMathHtml(String(q.correct_answer)) }} />
          }
        </div>
      </div>
    );
  };

  return (
    <MainLayout 
      user={user} 
      onLogout={onLogout} 
      sidebarContent={fromTeacher ? <TeacherSidebar /> : <StudentSidebar />}
    >
      <div className="mb-6 px-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="student-title">Exam Result</h1>
            {fromTeacher && studentName && (
              <p className="text-gray-600">Student: {studentName} {studentEmail ? `(${studentEmail})` : ''}</p>
            )}
            {(results[selectedAttemptIdx]?.student_exam || examTitle) && (
              <p className="text-gray-600">Exam: {results[selectedAttemptIdx]?.student_exam?.exam?.title || examTitle}</p>
            )}
          </div>
          <Button 
            onClick={handleBackClick}
            variant="secondary"
          >
            {fromTeacher ? 'Back to Exam Results' : 'Back to Exams'}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
        </div>
      ) : results.length > 0 ? (
        <Card>
          <div className="p-6">
            {/* Tabs for attempts */}
            {results.length > 1 && (
              <div className="flex space-x-2 mb-4">
                {results.map((attempt, idx) => (
                  <button
                    key={attempt.student_exam_id}
                    className={`px-4 py-2 rounded ${selectedAttemptIdx === idx ? 'bg-[#111827] text-white' : 'bg-gray-200'}`}
                    onClick={() => setSelectedAttemptIdx(idx)}
                  >
                    Attempt {attempt.attempt_number || idx + 1}
                  </button>
                ))}
              </div>
            )}

            {(() => {
              const selected = results[selectedAttemptIdx];
              const notPublished = selected?.status === 'not published' && !selected?.report;
              if (notPublished) {
                return (
                  <div className="mb-6">
                    <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-4 rounded">
                      <h2 className="text-lg font-semibold ">Results not published yet</h2>
                      {selected?.message && (
                        <p className="mb-2">{selected.message}</p>
                      )}
                      <div className="text-sm grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <span className="font-medium">Available on:</span> {formatDate(selected.publish_time)}
                        </div>
                        <div>
                          <span className="font-medium">Server time now:</span> {selected.current_time || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Time remaining:</span> {countdown ?? selected.time_remaining ?? 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2">
                Attempt {results[selectedAttemptIdx].attempt_number || selectedAttemptIdx + 1} Summary
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <span className="font-medium">Score:</span> {results[selectedAttemptIdx].score_percentage}%
                </div>
                <div>
                  <span className="font-medium">Obtained Marks:</span> {results[selectedAttemptIdx].obtained_marks} / {results[selectedAttemptIdx].max_marks}
                </div>
                <div>
                  <span className="font-medium">Status:</span> <span className={results[selectedAttemptIdx].passed_status ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{results[selectedAttemptIdx].passed_status ? 'Passed' : 'Failed'}</span>
                </div>
                <div>
                  <span className="font-medium">Date:</span> {formatDate(results[selectedAttemptIdx].created_at)}
                </div>
              </div>
            </div>
              );
            })()}

            {/* Detailed report for selected attempt */}
            {results[selectedAttemptIdx].report?.details && results[selectedAttemptIdx].report.details.length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-semibold mb-4">Detailed Report</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 border-b text-left">#</th>
                        <th className="px-4 py-2 border-b text-left">Type</th>
                        <th className="px-4 py-2 border-b text-left">Question</th>
                        <th className="px-4 py-2 border-b text-left">Status</th>
                        <th className="px-4 py-2 border-b text-left">Marks</th>
                        <th className="px-4 py-2 border-b text-left">Part</th>
                        <th className="px-4 py-2 border-b text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results[selectedAttemptIdx].report.details.map((q: any, idx: number) => (
                        <tr key={q.question_id} className="border-b last:border-b-0">
                          <td className="px-4 py-2">{idx + 1}</td>
                          <td className="px-4 py-2">{q.question_type}</td>
                          <td className="px-4 py-2">
                            <div dangerouslySetInnerHTML={{ __html: renderMathHtml(q.question_content) }} />
                          </td>
                          <td className="px-4 py-2">
                            <span className={
                              q.status === 'correct'
                                ? 'text-green-600 font-bold'
                                : q.status === 'wrong'
                                  ? 'text-red-600 font-bold'
                                  : 'text-gray-600'
                            }>
                              {q.status ? q.status.charAt(0).toUpperCase() + q.status.slice(1) : 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-2">{q.marks_obtained}</td>
                          <td className="px-4 py-2">{q.part || 'A'}</td>
                          <td className="px-4 py-2">
                            <Button size="sm" variant="primary" onClick={() => { setModalQuestion(q); setModalOpen(true); }}>View Answers</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <div className="text-center py-8">
          <div className="bg-yellow-100 text-yellow-800 p-4 rounded-lg">
            <h2 className="text-xl font-semibold">No Attempts Available</h2>
            <p className="mt-2">No attempts found for this exam.</p>
          </div>
        </div>
      )}

      {/* Modal for viewing answers */}
      {modalOpen && modalQuestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className="bg-white rounded-lg shadow-lg p-2 max-w-lg w-full relative overflow-hidden"
            style={{ maxHeight: '80vh' }}
          >
            <div className="flex items-center justify-between mb-4 sticky top-0 p-4 bg-white z-10  border-b">
              <h2 className="text-lg font-semibold">Answers for Question</h2>
            <button
                className="text-gray-600 hover:text-gray-600 transition-colors"
              onClick={() => setModalOpen(false)}
              aria-label="Close"
            >
                <svg 
                  className="w-6 h-6" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                </svg>
            </button>
            </div>
            <div ref={modalContentRef} className="mt-2 px-4 pb-4 overflow-x-auto" style={{ maxHeight: '61vh', overflowY: 'auto' }}>
              <div className="mb-2 text-gray-900 font-medium break-words">
              <div dangerouslySetInnerHTML={{ __html: renderMathHtml(modalQuestion.question_content) }} />
            </div>
            {renderAnswerDetails(modalQuestion)}
              {/* Solution / Note */}
              <div className="mt-6 pt-4 border-t break-words">
                <h3 className="text-md font-semibold mb-2">Solution</h3>
                {isSolutionLoading && (
                  <div className="text-gray-600">Loading solution...</div>
                )}
                {solutionError && (
                  <div className="text-red-600 text-sm">{solutionError}</div>
                )}
                {!isSolutionLoading && !solutionError && (
                  solutionHtml ? (
                    <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: renderMathHtml(solutionHtml) }} />
                  ) : (
                    <div className="text-gray-600">No solution provided.</div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ExamResult;
